import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {useProduct} from 'coreHooks';
import useGeolocalizedSizes from 'coreHooks/useGeolocalizedSizes';

import ColorDescription from 'brandComponents/ColorDescription';
import Money from 'brandComponents/Money';
import Picture from 'brandComponents/Picture';
import {PRODUCT_IMAGES_SETTINGS, ViewType} from 'brandConstants';

import {IOrderDetailsItemProps} from './IOrderDetailsItem';
import messages from './messages';

const OrderDetailsItem = ({classNames, productItem, currency, shippingCountryCode}: IOrderDetailsItemProps) => {
	const intl = useIntl();
	const product = useProduct(productItem.getProductId());
	const variationGroup = useProduct(product.getVariationGroupId());
	const isBonus = productItem.isBonus();
	const geolocalizedSize = useGeolocalizedSizes(product, {countryCode: shippingCountryCode});

	const price = useMemo(() => {
		const priceAfterOrderDiscount = productItem.getPriceAfterOrderDiscount();
		const proratedPrice = productItem.getProratedPriceAdjustmentPrices();
		const hasProratedPrice = proratedPrice && proratedPrice !== productItem.getPrice();
		const hasPriceAfterDiscount = priceAfterOrderDiscount && priceAfterOrderDiscount !== productItem.getPrice();
		const hasAnOrderDiscount: boolean = hasPriceAfterDiscount || hasProratedPrice;

		if (hasAnOrderDiscount) {
			return proratedPrice || priceAfterOrderDiscount;
		}

		return  productItem.getPriceAfterItemDiscount() ?? productItem.getPrice();
	}, [productItem.getProductId()]);

	return (
		<div key={productItem.getItemId()} className={cn(classNames.item, 'order-details__item')}>
			<Picture
				image={variationGroup.getProductImage({
					viewType: ViewType.ZOOM,
					transformationObject: PRODUCT_IMAGES_SETTINGS.myAccount,
				})}
				classNames={classNames.image}
			/>
			{product && (
				<ul className="order-details__item__details">
					<li className="order-details__item__details-brand">{product.getBrand()}</li>
					<li>{product.getName()}</li>
					<li>
						<ColorDescription product={product} />
					</li>
					<li>
						{intl.formatMessage(messages.size, {
							size: geolocalizedSize[product.getProductId()],
						})}
					</li>
					{!isBonus && (
						<li>
							<Money
								// Because 0 is considered false but is a possible price,
								// you need to check explicitly for null or undefined
								value={price}
								currency={currency}
								showLocalizedPrices={false}
								spaceBetweenCurrencyAndPrice={false}
							/>
						</li>
					)}
					<li>{intl.formatMessage(messages.quantity, {qty: productItem.getQuantity()})}</li>
					<li>{intl.formatMessage(messages.productId, {productId: productItem.getProductId()})}</li>
				</ul>
			)}
		</div>
	);
};

export default OrderDetailsItem;
